import {
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { Observable } from 'rxjs';
import { CreateJournalCommand, Journal, JournalEntry, JournalEntryCommand, JournalSide, JournalsService, JournalStatusType, JVType, SetJournalStatusCommand, AccountsService, CurrenciesService } from '@api-accounting-client';
import { FormBaseComponent } from '../../shared/bases';
import { DialogExtendedService, NotificationsExtendedService } from '../../shared/services';
import { DataResult } from '../../shared/models/data-result/data-result';
import { DeleteDialogComponent } from '../../shared/components/dialogs/warning';
import { JournalEditableGridModel } from './journalEditableGridModel';
import { JournalEditableGridComponent } from '../../shared/components/grids/editable-grids';
import { PermissionStore } from '../../shared/services/permission/permission-store.service';
import { tenantNow } from '@app/shared/helpers/tenant-time';
@Component({
    template: '',
    standalone: false
})
export abstract class JournalsBaseComponent
  extends FormBaseComponent<any>
  implements OnInit, OnDestroy {
  @ViewChild('JournalEditableGrid')
  JournalEditableGrid: JournalEditableGridComponent;
  journal: Journal;
  reference: any;
  formArray: UntypedFormArray;
  gridData: JournalEditableGridModel[] = [];
  public min: Date = new Date(1, 1, 10);

  isSameProject = true;
  projectsData = [];

  // JV Types for dropdown
  jvTypes = [];

  /**
   * ZEY-1966: injected rather than taken as a constructor parameter so the add and edit
   * components' own constructors stay untouched — both templates read
   * `permissionStore.journalPermissions$` to gate their action bars.
   */
  public permissionStore = inject(PermissionStore);

  @Input() journalUid: string;

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected journalsService: JournalsService,
    protected dialogService: DialogService,
    protected accountsService: AccountsService,
    protected currenciesService: CurrenciesService,
    protected dialogExtendedService: DialogExtendedService,
    protected notificationsExtendedService: NotificationsExtendedService,
  ) {
    super();
  }

  onLoadData(): any {
    return {};
  }

  protected populateJournalEntries(items: JournalEntry[]) {
    let gridRecord: JournalEditableGridModel;
    this.gridData = [];
    items.forEach((item, index) => {
      gridRecord = {
        rowId: index,
        accountUid: item?.account?.uid,
        accountCode: item?.account?.code,
        accountName: item?.account?.name,
        accountDetails: {
          fullName: item.account?.fullName,
          accountRootType: item.account?.accountRootType,
          accountType: item.account?.accountType?.name,
          accountSubType: item.account?.accountSubType?.name
        },
        remark: item.note,
        projectUid: item.project?.uid,
        debit: 0,
        credit: 0,
        currency: item.currency?.uid,
        rate: item.currencyRate,
        baseAmount: item.baseAmount,
        parityRate: item.parityRate,
        baseAmount2: item.baseAmount2,
        side: item.side
      };
      item.side === JournalSide.Debit
        ? (gridRecord.debit = item.amount)
        : (gridRecord.credit = item.amount);
      this.gridData.push(gridRecord);

      // Check if Project is same for all line items
      if (gridRecord.projectUid !== items[0].project?.uid) {
        this.isSameProject = false;
      }
    });

    // Populate the same project value on Main Project field
    if (this.isSameProject) {
      this.form.patchValue({
        projectUid: items[0].project?.uid
      });
    }
  }

  protected async getJournal(journalUid: string) {
    return await this.journalsService
    .journalsUidGet(journalUid)
    .toPromise();
  }

  async submitRecord(status: string, _mode?: string, _actionName?: string): Promise<void> {
    super.submit();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.formArray = this.JournalEditableGrid.getData();
    for (const form of (this.formArray.controls as UntypedFormGroup[]).slice(0, -1)) {

      if (form.invalid) {
        this.dialogExtendedService.showValidationDialog(
          'yourFormIsNotValid'
        );
        return;
      }

    }

    if (this.JournalEditableGrid.hasUnbalancedSides()) {
      this.dialogExtendedService.showValidationDialog(
        'sidesAreNotEqual'
      );
      return;
    }

    const journalEntryItems: JournalEntryCommand[] = [];
    let item: JournalEntryCommand;
    for (const form of (this.formArray.controls as UntypedFormGroup[]).slice(0, -1)) {
      item = {
        accountUid: form.value.accountUid.trim(),
        note: form.value.remark,
        // If line item has a project then use it
        // otherwise use project from Main field
        projectUid: form.value.projectUid ?? this.form.value.projectUid,
        currencyUid: form.value.currency,
        currencyRate: form.value.rate,
        baseAmount: Math.abs(form.value.baseAmount),
        baseAmount2: this.allowDualCurrency ? Math.abs(form.value.baseAmount2) : 0,
        parityRate: this.allowDualCurrency ? form.value.parityRate : 0,
        side: form.value.side
      };

      if (form.value.debit) {
        item.amount = form.value.debit;
      } else {
        item.amount = form.value.credit;
      }
      journalEntryItems.push(item);
    }

    if (journalEntryItems.length === 0) {
      this.dialogExtendedService.showValidationDialog(
        'youHaveNoJournalEntryItemsAdded'
      );
      return;
    }

    const command: CreateJournalCommand = {
      reference: this.form.value.reference,
      date: this.form.value.date,
      note: this.form.value.note,
      jvType: this.form.value.jvType,
      items: journalEntryItems
    };
    // No status here: ZEY-1963 moved it server-side, and CreateJournalCommand no longer
    // carries it. A manual JV is born Draft and reaches the books through PATCH /Journals/Status.

    this.isWaiting = true;
    try {
      const response = await this.onDataSubmit(command).toPromise();
      this.journalUid = response.uid ?? this.journalUid;

      this.isDirty = false;

      // ZEY-1966: post only when the user actually asked to post.
      //
      // This used to read `if (status != 'Draft')` and then PATCH `Posted` for 'Posted' and
      // `Unknown` for anything else. Unknown is not a status a journal may be set to -- the server
      // now rejects it outright (SetJournalStatusCommandValidator accepts Posted and nothing else)
      // and CK_Journals_Status forbids it at the table. Every caller happens to pass 'Draft' or
      // 'Posted' today, so the Unknown branch was unreachable, but it was a landmine: adding any
      // third action name would have silently sent a status the server refuses, and the user would
      // have seen their entry save and then fail to post with no obvious cause.
      //
      // Saving is now just the create -- a manual JV is born Draft and the server owns that
      // (ZEY-1963) -- and posting is the create followed by the PATCH.
      if (status === 'Posted') {
        const command: SetJournalStatusCommand = { status: JournalStatusType.Posted };
        await this.onStatusSubmit(command).toPromise();
      }

      this.isWaiting = false;

      if (_actionName == 'Save and Close') {
        this.goToViewPage(this.journalUid);
      }

      if (_actionName == 'Save and New') {
        if (_mode === 'add') {
          this.notificationsExtendedService.showNotification(
            this.translate.instant('journalHasBeenCreated')
          );
          this.reloadRoute();
        } else if (_mode === 'edit') {
          this.goToAddPage();
        }
      }

      if (_actionName == 'Post' && status === 'Posted' && this.journalUid) {
        this.goToViewPage(this.journalUid);
      }
    } catch (error) {
      this.isWaiting = false;
      this.isDirty = false;
      
      if (error.status === 403) {
        this.dialogExtendedService.showNotAuthorizedDialog();
      }
      return;
    } 
  }

  dirtyFormHandler(isDirty: boolean): void {
    if (isDirty) {
      this.isDirty = true;
    }
  }

  goToListPage(): void {
    this.router.navigate(['/journals']);
  }

  goToAddPage(): void {
    this.router.navigate(['/journals/add']);
  }

  goToViewPage(event: any): void {
    this.router.navigate(['/journals/view'], {
      queryParams: { id: event }
    });
  }

  onActionClick(value: any, _mode?: string) {
    switch (value) {
      case 'Save and Close':
        this.submitRecord('Draft', _mode, value);
        break;
      case 'Save and New':
        this.submitRecord('Draft', _mode, value);
        break;
      case 'Cancel':
        this.appSettingsService.navigateBack();
        break;
      case 'Discard':
        this.goToListPage();
        break;
      case 'Post':
        this.submitRecord('Posted', _mode, value);
        break;
    }
  }

  abstract onDataSubmit<TCommand extends unknown>(
    command: TCommand
  ): Observable<any>;

  abstract onStatusSubmit<TCommand extends unknown>(
    status: TCommand
  ): Observable<any>;

  protected abstract onDeleteRecord(
    uid: string
  ): Observable<DataResult<any>>;

  private initJvTypes() {
    this.jvTypes = [
      { value: JVType.JV, label: this.translate.instant('jvTypeGeneralJournal'), code: 'JV' },
      { value: JVType.PR, label: this.translate.instant('jvTypePayroll'), code: 'PR' },
      { value: JVType.BNK, label: this.translate.instant('jvTypeBank'), code: 'BNK' },
      { value: JVType.INV, label: this.translate.instant('jvTypeInventory'), code: 'INV' },
      { value: JVType.WST, label: this.translate.instant('jvTypeWaste'), code: 'WST' },
      { value: JVType.DEP, label: this.translate.instant('jvTypeDepreciation'), code: 'DEP' },
      { value: JVType.MISC, label: this.translate.instant('jvTypeMiscellaneous'), code: 'MISC' }
    ];
  }

  getJvTypeLabel(jvType: string): string {
    const match = this.jvTypes.find(t => t.value === jvType);
    return match ? `${match.code} - ${match.label}` : jvType;
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.initJvTypes();
    // Rebuild the JV-type labels on language change; translate.instant() is a
    // one-shot read so without this the dropdown keeps the original-language
    // (or raw-key) labels after the user switches language.
    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => this.initJvTypes())
    );
    this.form = this.formBuilder.group({
      date: [tenantNow(), [Validators.required]],
      reference: [null, Validators.pattern(/^[a-zA-Z0-9_-]*$/)], // [[null], [Validators.required]],
      note: [null, [Validators.required]],
      projectUid: [null],
      jvType: [null]
    });

    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.isDirty = true;
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  async removeRecord(dataItem: any): Promise<void> {
    const dialogRef: DialogRef = this.dialogService.open({
      content: DeleteDialogComponent,
      width: 600
    });
    dialogRef.result.subscribe(async (result) => {
      if ((result as boolean) === true) {
        await this.onDeleteRecord(dataItem.uid).toPromise();
        this.router.navigate(['/journals']);
      }
    });
  }

  @HostListener('document:keydown.escape') onEscapeHandler(): void {
    if (!this.dialogExtendedService.closeValidationDialog()) {
      if (!this.isDrawerOpened) {
        this.goToListPage();
      }
    }
  }

}
